import { useCallback, useMemo, useState } from "react";

/**
 * Memoizes the localStorage value and provides a setter to update it.
 */
export function useLocalStorage<T>(keyName: string, defaultValue: T): [T, Setter<T>] {
	const [storedValue, setStoredValue] = useState(() => {
		const value = window.localStorage.getItem(keyName);
		try {
			if (value == null) {
				window.localStorage.setItem(keyName, JSON.stringify(defaultValue));
				return defaultValue;
			}

			return JSON.parse(value) as T;
		} catch {
			return defaultValue;
		}
	});
	const setValue: Setter<T> = useCallback(
		(newValue: T | ((prev: T) => T)) => {
			if (typeof newValue === "function") {
				const func = newValue as (prev: T) => T;
				setStoredValue((prev) => {
					const updatedValue = func(prev);
					window.localStorage.setItem(keyName, JSON.stringify(updatedValue));
					return updatedValue;
				});
			} else {
				window.localStorage.setItem(keyName, JSON.stringify(newValue));
				setStoredValue(newValue);
			}
		},
		[keyName],
	);

	return useMemo(() => [storedValue, setValue], [storedValue, setValue]);
}
